import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Download, AlertTriangle, Loader2 } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SeoHead from "@/components/site/SeoHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Wird nach Klick auf den Bestätigungs-Link in der DOI-Mail aufgerufen.
 * Backend redirected hierher mit ?status=ok&magnet=... oder ?status=invalid.
 */
export default function LeadmagnetConfirmed() {
  const [params] = useSearchParams();
  const status = params.get("status");
  const magnetId = params.get("magnet") || "kurskatalog-pdf";
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status !== "ok") return;
    if (magnetId === "kurskatalog-pdf" || magnetId.startsWith("bundle:")) {
      setMeta({ title: magnetId.startsWith("bundle:") ? "Dein Bundle" : "Kurskatalog 2026 (Broschüre)" });
      return;
    }
    setBusy(true);
    axios.get(`${API}/leadmagnet/${magnetId}/meta`).then((r) => {
      setMeta(r.data);
    }).catch(() => setMeta({ title: "Dein Leitfaden" })).finally(() => setBusy(false));
  }, [magnetId, status]);

  const downloadUrl =
    magnetId === "kurskatalog-pdf"
      ? `${process.env.REACT_APP_BACKEND_URL}/api/brochure/download`
      : `${process.env.REACT_APP_BACKEND_URL}/api/leadmagnet/${magnetId}/download`;

  return (
    <div className="min-h-screen bg-cream-50 flex flex-col">
      <SeoHead title="E-Mail bestätigt · Dickies-Campus" description="Dein Leitfaden steht bereit zum Download." />
      <Header />
      <main className="flex-1 flex items-center py-20 px-4">
        <div className="max-w-2xl mx-auto w-full">
          {status === "invalid" ? (
            <div className="rounded-3xl bg-white border-2 border-clay/40 p-10 text-center shadow-soft" data-testid="leadmagnet-confirm-invalid">
              <AlertTriangle className="h-12 w-12 text-clay mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="font-display text-3xl text-sand-800 mb-3 font-normal">Link ungültig oder abgelaufen</h1>
              <p className="text-sand-600 text-base leading-relaxed mb-6">
                Der Bestätigungs-Link ist nicht (mehr) gültig. Das kann passieren, wenn
                er älter als 7 Tage ist oder schon verwendet wurde.
              </p>
              <Link
                to="/leitfaeden"
                data-testid="leadmagnet-confirm-back"
                className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 py-3 font-semibold transition-colors"
              >
                Zurück zu den Leitfäden
              </Link>
            </div>
          ) : (
            <div className="rounded-3xl bg-white border-2 border-sage/40 p-10 text-center shadow-soft" data-testid="leadmagnet-confirm-success">
              <CheckCircle2 className="h-12 w-12 text-sage-dark mx-auto mb-4" strokeWidth={1.5} />
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-sage-dark mb-2">
                E-Mail bestätigt
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-sand-800 mb-4 font-normal leading-tight">
                Vielen Dank — dein Leitfaden ist bereit.
              </h1>
              <p className="text-sand-600 text-base leading-relaxed mb-7 max-w-md mx-auto">
                {busy ? "Wir bereiten alles vor …" : (
                  <>
                    {meta?.title && <span className="block font-semibold text-sand-800 mb-2">{meta.title}</span>}
                    Du kannst dein PDF jetzt direkt herunterladen. Zusätzlich haben wir
                    dir den Link auch per E-Mail geschickt — falls du es später noch einmal
                    brauchst.
                  </>
                )}
              </p>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leadmagnet-confirm-download"
                className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-8 py-3.5 text-base font-semibold transition-all shadow-[0_4px_14px_rgba(190,53,25,0.35)]"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
                Jetzt herunterladen
              </a>
              <div className="mt-8 pt-6 border-t border-cream-300 text-sm text-sand-600">
                <p className="mb-2">Möchtest du noch mehr Leitfäden ansehen?</p>
                <Link to="/leitfaeden" data-testid="leadmagnet-confirm-more"
                  className="text-terra font-semibold underline underline-offset-2 hover:text-terra-dark">
                  Alle kostenlosen Leitfäden →
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
